import * as chrono from 'chrono-node';
import {
  findStartOfNextWeek,
  findStartOfNextMonth,
  setHoursToNine,
} from './snoozeHelpers';

const DURATION_RE =
  /\b(?:em|daqui\s+a)\s+(\d+)\s*(minutos?|min|horas?|h|dias?|semanas?)\b/i;

// "em 3 horas", "daqui a 30 min", "em 2 dias", "em 1 semana"
const parseDuration = (text, ref) => {
  const match = text.match(DURATION_RE);
  if (!match) return null;
  const amount = parseInt(match[1], 10);
  const unit = match[2].toLowerCase();
  const date = new Date(ref.getTime());
  if (unit.startsWith('min')) {
    date.setMinutes(date.getMinutes() + amount);
  } else if (unit === 'h' || unit.startsWith('hora')) {
    date.setHours(date.getHours() + amount);
  } else if (unit.startsWith('dia')) {
    date.setDate(date.getDate() + amount);
  } else if (unit.startsWith('semana')) {
    date.setDate(date.getDate() + amount * 7);
  }
  return date;
};

const nextSaturdayAtNine = ref => {
  const date = new Date(ref.getTime());
  let add = (6 - date.getDay() + 7) % 7;
  if (add === 0) add = 7; // hoje é sábado -> próximo sábado
  date.setDate(date.getDate() + add);
  return setHoursToNine(date);
};

const endOfMonthAtNine = ref => {
  const date = new Date(ref.getFullYear(), ref.getMonth() + 1, 0); // último dia do mês
  return setHoursToNine(date);
};

// "semana que vem", "fim do mês", etc -> data computada direto
const parseIdiom = (text, ref) => {
  if (/\b(semana que vem|pr[oó]xima semana)\b/.test(text)) {
    return setHoursToNine(findStartOfNextWeek(ref));
  }
  if (/\b(m[eê]s que vem|pr[oó]ximo m[eê]s)\b/.test(text)) {
    return setHoursToNine(findStartOfNextMonth(ref));
  }
  if (/\b(fim|final) de semana\b/.test(text)) {
    return nextSaturdayAtNine(ref);
  }
  if (/\b(fim|final) do m[eê]s\b/.test(text)) {
    return endOfMonthAtNine(ref);
  }
  return null;
};

const pad2 = num => String(num).padStart(2, '0');

// "dia 25" -> "25/06" (rola pro mês seguinte se o dia já passou)
const rewriteDayOfMonth = (text, ref) => {
  return text.replace(/\bdia\s+(\d{1,2})\b/i, (full, dd) => {
    const day = parseInt(dd, 10);
    let month = ref.getMonth();
    if (day < ref.getDate()) {
      month += 1;
    }
    // month pode chegar a 12 (virada de ano); pad2(13) gera "13" e o chrono
    // trata como inválido -> cai em null. Edge aceitável para virada de ano.
    return `${pad2(day)}/${pad2(month + 1)}`;
  });
};

// "9h" -> "9:00", "14h30" -> "14:30", "9 h" -> "9:00", "9hs" -> "9:00"
const normalizeHourNotation = text =>
  text.replace(
    /\b(\d{1,2})\s*h(?:s|oras?)?\s*(\d{2})?\b/gi,
    (full, hh, mm) => `${hh}:${mm || '00'}`
  );

// garante que um horário "HH:MM" seja precedido de "às" (estabiliza o chrono
// ao associar a hora ao dia nomeado, ex.: "sexta 14:00" -> "sexta às 14:00")
const ensureAsBeforeTime = text =>
  text
    .replace(/(?<!às\s)\b(\d{1,2}:\d{2})\b/gi, 'às $1')
    .replace(/\bàs\s+às\s+/gi, 'às ');

/**
 * Converte texto em português numa Date futura, ou null.
 * @param {string} text
 * @param {Date} [referenceDate=new Date()]
 * @returns {Date|null}
 */
export const parseNaturalSnooze = (text, referenceDate = new Date()) => {
  if (!text || !text.trim()) {
    return null;
  }
  const normalizedText = text.trim().toLowerCase();

  let result =
    parseDuration(normalizedText, referenceDate) ||
    parseIdiom(normalizedText, referenceDate);

  if (!result) {
    let chronoText = rewriteDayOfMonth(normalizedText, referenceDate);
    chronoText = normalizeHourNotation(chronoText);
    chronoText = ensureAsBeforeTime(chronoText);
    result = chrono.pt.parseDate(chronoText, referenceDate, {
      forwardDate: true,
    });
  }

  if (!result) {
    return null;
  }
  // Regra de validade: apenas futuro estrito. Diferente do calendário do modal
  // (que desabilita slots < agora+1h via disabledTime), o texto é intenção
  // explícita do usuário — "em 30 minutos" deve valer. A trava de +1h é só do
  // seletor manual de data, não desta entrada por texto.
  if (result.getTime() <= referenceDate.getTime()) {
    return null;
  }
  return result;
};
